import { useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { MessageSquare, ShoppingCart, Truck, Users, Settings, Menu, BookOpen } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import SettingsLayout from '@/pages/SettingsLayout';
import PricingPage from '@/pages/PricingPage';
import PromosPage from '@/pages/PromosPage';
import ImagesPage from '@/pages/ImagesPage';
import PreviewPage from '@/pages/PreviewPage';
import AuditPage from '@/pages/AuditPage';
import BrandSettingsPage from '@/pages/BrandSettingsPage';
import ReplyTemplatesPage from '@/pages/ReplyTemplatesPage';
import TriggersPage from '@/pages/TriggersPage';
import ColorsPage from '@/pages/ColorsPage';
import SizeRulesPage from '@/pages/SizeRulesPage';
import KbPage from '@/pages/KbPage';
import IntentsPage from '@/pages/IntentsPage';
import PagesSettingsPage from '@/pages/PagesSettingsPage';
import AdminsPage from '@/pages/AdminsPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AdsPage from '@/pages/AdsPage';
import ComposeSettingsPage from '@/pages/ComposeSettingsPage';
import FaqImportPage from '@/pages/FaqImportPage';
import OrdersPage from '@/pages/OrdersPage';
import TrackingPage from '@/pages/TrackingPage';
import SlipTestPage from '@/pages/SlipTestPage';
import FaqPage from '@/pages/FaqPage';
import AnswersPage from '@/pages/AnswersPage';
import BrandsPage from '@/pages/BrandsPage';
import OrderBuilderPage from '@/pages/OrderBuilderPage';
import { AuthGuard } from '@/components/AuthGuard';
import { UserMenu } from '@/components/UserMenu';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

function NavItem({ to, icon, label, onClick }: { to: string; icon: React.ReactNode; label: string; onClick?: (() => void) | undefined }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-4 py-2 rounded-lg transition text-sm',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent',
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

function Sidebar({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  return (
    <aside className={cn('w-60 shrink-0 h-full border-r border-border p-4 flex-col gap-1', className)}>
      <div className="px-4 py-3 mb-2">
        <h1 className="text-xl font-bold">CHER Bot</h1>
        <p className="text-xs text-muted-foreground">Admin Dashboard</p>
      </div>
      <NavItem to="/" icon={<MessageSquare size={18} />} label="Chats" onClick={onNavigate} />
      <NavItem to="/orders" icon={<ShoppingCart size={18} />} label="Orders" onClick={onNavigate} />
      <NavItem to="/tracking" icon={<Truck size={18} />} label="Tracking" onClick={onNavigate} />
      <NavItem to="/customers" icon={<Users size={18} />} label="Customers" onClick={onNavigate} />
      <NavItem to="/settings" icon={<Settings size={18} />} label="Settings" onClick={onNavigate} />
      <div className="flex-1" />
      <a
        href="/user-guide.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-foreground hover:bg-accent"
      >
        <BookOpen size={18} />
        <span>📖 คู่มือการใช้ (PDF)</span>
      </a>
      <UserMenu />
    </aside>
  );
}

const ChatsPage = () => <div className="text-muted-foreground">Chats — TODO Phase 2</div>;
const CustomersPage = () => <div className="text-muted-foreground">Customers — TODO</div>;

function Shell() {
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Desktop: sidebar คงที่ */}
      <Sidebar className="hidden md:flex bg-muted/40" />
      {/* Mobile: drawer ทับ + overlay */}
      {navOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNavOpen(false)} />
          <Sidebar className="relative z-50 flex bg-background shadow-xl" onNavigate={() => setNavOpen(false)} />
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile: แถบบน + ปุ่มแฮมเบอร์เกอร์ */}
        <header className="md:hidden flex items-center gap-3 border-b border-border px-4 py-3">
          <button onClick={() => setNavOpen(true)} aria-label="เปิดเมนู" className="-ml-1 p-1 rounded-md hover:bg-accent">
            <Menu size={22} />
          </button>
          <span className="font-bold">CHER Bot</span>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto min-w-0">
          <Routes>
            <Route path="/" element={<ChatsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/tracking" element={<TrackingPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="pricing" replace />} />
              <Route path="brands" element={<BrandsPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="promos" element={<PromosPage />} />
              <Route path="images" element={<ImagesPage />} />
              <Route path="brand" element={<BrandSettingsPage />} />
              <Route path="answers" element={<AnswersPage />} />
              <Route path="order-builder" element={<OrderBuilderPage />} />
              <Route path="templates" element={<ReplyTemplatesPage />} />
              <Route path="triggers" element={<TriggersPage />} />
              <Route path="colors" element={<ColorsPage />} />
              <Route path="sizes" element={<SizeRulesPage />} />
              <Route path="pages" element={<PagesSettingsPage />} />
              <Route path="admins" element={<AdminsPage />} />
              <Route path="preview" element={<PreviewPage />} />
              <Route path="slip" element={<SlipTestPage />} />
              <Route path="faq" element={<FaqPage />} />
              <Route path="kb" element={<KbPage />} />
              <Route path="intents" element={<IntentsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="ads" element={<AdsPage />} />
              <Route path="compose" element={<ComposeSettingsPage />} />
              <Route path="faq-import" element={<FaqImportPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthGuard>
        <Shell />
      </AuthGuard>
      <Toaster />
    </QueryClientProvider>
  );
}
